import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useNativePlugins } from '../hooks/useNativePlugins';
import { Camera, Share, Download, FileText, Smartphone } from 'lucide-react';

export function NativeFeaturesDemo() {
  const {
    platformInfo,
    isLoading,
    isNative,
    platform,
    deviceInfo,
    takePhoto,
    selectPhoto,
    saveFile,
    shareContent,
    showToast,
    triggerHaptic,
  } = useNativePlugins();

  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleTakePhoto = async () => {
    setIsProcessing(true);
    try {
      await triggerHaptic('light');
      const imageUri = await takePhoto();
      if (imageUri) {
        setSelectedImage(imageUri);
        await showToast('Photo taken successfully!');
      }
    } catch (error) {
      console.error('Error taking photo:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSelectPhoto = async () => {
    setIsProcessing(true);
    try {
      await triggerHaptic('light');
      const imageUri = await selectPhoto();
      if (imageUri) {
        setSelectedImage(imageUri);
        await showToast('Photo selected successfully!');
      }
    } catch (error) {
      console.error('Error selecting photo:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveData = async () => {
    setIsProcessing(true);
    try {
      const data = JSON.stringify({
        timestamp: new Date().toISOString(),
        platform: platform,
        deviceInfo: deviceInfo,
        selectedImage: selectedImage,
      }, null, 2);

      const success = await saveFile(data, `health-data-${Date.now()}.json`);
      if (success) {
        await showToast('Data saved successfully!');
        await triggerHaptic('medium');
      }
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleShare = async () => {
    setIsProcessing(true);
    try {
      await triggerHaptic('light');
      const success = await shareContent(
        'Healthscores.ai Data',
        'Check out my health data from the Healthscores.ai app!',
        'https://healthscores.ai'
      );
      if (success) {
        await showToast('Content shared successfully!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Loading platform information...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            Platform Information
          </CardTitle>
          <CardDescription>
            Current platform and device information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Badge variant={isNative ? 'default' : 'secondary'}>
              {isNative ? 'Native App' : 'Web App'}
            </Badge>
            <Badge variant="outline">
              {platform}
            </Badge>
            {deviceInfo && (
              <Badge variant="outline">
                {deviceInfo.manufacturer} {deviceInfo.model}
              </Badge>
            )}
          </div>
          
          {deviceInfo && (
            <div className="text-sm text-muted-foreground space-y-1">
              <div>OS: {deviceInfo.operatingSystem} {deviceInfo.osVersion}</div>
              <div>Platform: {deviceInfo.platform}</div>
              <div>Virtual: {deviceInfo.isVirtual ? 'Yes' : 'No'}</div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Camera Features
          </CardTitle>
          <CardDescription>
            Take photos or select from gallery (native only)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button 
              onClick={handleTakePhoto} 
              disabled={!isNative || isProcessing}
              className="flex-1"
            >
              <Camera className="h-4 w-4 mr-2" />
              Take Photo
            </Button>
            <Button 
              onClick={handleSelectPhoto} 
              disabled={!isNative || isProcessing}
              variant="outline"
              className="flex-1"
            >
              <FileText className="h-4 w-4 mr-2" />
              Select Photo
            </Button>
          </div>
          
          {selectedImage && (
            <div className="mt-4">
              <img 
                src={selectedImage} 
                alt="Selected" 
                className="w-full max-w-sm rounded-lg border"
              />
            </div>
          )}
          
          {!isNative && (
            <div className="text-sm text-muted-foreground">
              Camera features are only available in the native mobile app
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            File Operations
          </CardTitle>
          <CardDescription>
            Save and share data using native capabilities
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button 
              onClick={handleSaveData} 
              disabled={isProcessing}
              className="flex-1"
            >
              <Download className="h-4 w-4 mr-2" />
              Save Data
            </Button>
            <Button 
              onClick={handleShare} 
              disabled={isProcessing}
              variant="outline"
              className="flex-1"
            >
              <Share className="h-4 w-4 mr-2" />
              Share
            </Button>
          </div>
          
          <div className="text-sm text-muted-foreground">
            {isNative 
              ? 'Data will be saved to device storage' 
              : 'Data will be downloaded to your device'
            }
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
